import { isUndefined } from 'lodash';
import React, { ReactNode } from 'react';
import { Form, FormControlProps } from 'react-bootstrap';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { colors } from '~/styles';
import { EnterKeyHint, InputType, TypographySize } from '~/types';
import Label from '../Label';
import Typography from '../Typography';
import './input.scss';

interface Props extends FormControlProps {
    className?: string;
    type?: InputType;
    placeholder?: string;
    label?: string;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    isShowPassword: boolean;
    onToggleShowPassword: () => void;
    message?: string;
    isInvalid?: boolean;
    defaultValue?: string | number;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    name: string;
    required?: boolean;
    pattern?: string;
    min?: number | string;
    max?: number | string;
    minLength?: number;
    maxLength?: number;
    value?: any;
    isTextArea?: boolean;
    innerRef?: any;
    onBlur?: () => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
    enterKeyHint?: EnterKeyHint;
    autoFocus?: boolean;
    disabled?: boolean;
    editable?: boolean;
    info?: string;
    labelSize?: TypographySize;
    editableSameStyle?: boolean;
    hideMaxLengthCount?: boolean;
}

export default function Input({
    className = '',
    type = 'text',
    size,
    placeholder,
    label,
    leftIcon,
    rightIcon,
    isShowPassword,
    onToggleShowPassword,
    message,
    isInvalid,
    defaultValue,
    id,
    onChange,
    name,
    required,
    pattern,
    min,
    max,
    minLength,
    maxLength,
    value,
    isTextArea,
    innerRef,
    onBlur,
    onKeyDown,
    onFocus,
    onClick,
    enterKeyHint,
    autoFocus,
    disabled,
    editable = true,
    info,
    labelSize,
    editableSameStyle,
    hideMaxLengthCount = false,
}: Props): JSX.Element {
    const isPassword = type === 'password';

    return (
        <div className={`input ${className}`}>
            {!!label && <Label fontSize={labelSize}>{label}</Label>}
            {!!info && (
                <Typography className='input__info' color='neutral-300'>
                    {info}
                </Typography>
            )}
            <div className='input__wrapper'>
                {!!leftIcon && <div className='input__wrapper-icon__left'>{leftIcon}</div>}
                {isTextArea ? (
                    <Form.Control
                        ref={innerRef}
                        type={isPassword && isShowPassword ? 'text' : type}
                        size={size}
                        placeholder={placeholder}
                        defaultValue={defaultValue}
                        className={`
                    ${leftIcon ? 'input__wrapper-padded__left' : ''}
                    ${rightIcon || isPassword ? 'input__wrapper-padded__right' : ''}
                    ${isInvalid ? 'in-valid' : ''}
                    ${!editable && !editableSameStyle ? 'input__wrapper--not-editable' : ''}`}
                        onChange={onChange}
                        id={id}
                        name={name}
                        required={required}
                        minLength={minLength}
                        maxLength={maxLength}
                        value={value}
                        as='textarea'
                        disabled={disabled || !editable}
                        title={info}
                    />
                ) : (
                    <Form.Control
                        onBlur={onBlur}
                        onKeyDown={onKeyDown}
                        onFocus={onFocus}
                        onClick={onClick}
                        enterKeyHint={enterKeyHint}
                        autoFocus={autoFocus}
                        ref={innerRef}
                        type={isPassword && isShowPassword ? 'text' : type}
                        size={size}
                        placeholder={placeholder}
                        defaultValue={defaultValue}
                        className={`
                    ${leftIcon ? 'input__wrapper-padded__left' : ''}
                    ${rightIcon || isPassword ? 'input__wrapper-padded__right' : ''}
                    ${isInvalid ? 'in-valid' : ''}
                    ${!editable && !editableSameStyle ? 'input__wrapper--not-editable' : ''}`}
                        onChange={onChange}
                        id={id}
                        name={name}
                        required={required}
                        pattern={pattern}
                        min={min}
                        max={max}
                        minLength={minLength}
                        maxLength={maxLength}
                        value={value}
                        disabled={disabled || !editable}
                        title={info}
                    />
                )}

                {(!!rightIcon || isPassword) && (
                    <div className='input__wrapper-icon__right'>
                        {isPassword && !rightIcon ? (
                            <span
                                className='input__wrapper-icon__right-password'
                                onClick={onToggleShowPassword}
                            >
                                {isShowPassword ? (
                                    <MdVisibilityOff size={20} color={colors['neutral-300']} />
                                ) : (
                                    <MdVisibility size={20} color={colors['neutral-300']} />
                                )}
                            </span>
                        ) : (
                            rightIcon
                        )}
                    </div>
                )}
            </div>
            {!isUndefined(maxLength) && !hideMaxLengthCount && !message && (
                <Typography size={12} color='neutral-300'>
                    {maxLength - (value?.length || 0)}
                </Typography>
            )}
            {!!message && (
                <Typography
                    size={12}
                    color={isInvalid ? 'error' : 'neutral-300'}
                    className='input__wrapper-message'
                >
                    {message}
                </Typography>
            )}
        </div>
    );
}
